import React, { useEffect, useRef, useState } from 'react';
import './motion.css';
import { cn, initials } from './model/derive';

const TONE = {
  gray: {
    box: 'bg-grayscale-gray01 text-text-tertiary shadow-[inset_0_0_0_1px_#d5d2cd]',
    dot: 'bg-grayscale-gray05',
  },
  // 점 없는 회색 테두리 — 상태가 아닌 구분 라벨 (Staff)
  outline: {
    box: 'bg-grayscale-gray01 text-text-secondary shadow-[inset_0_0_0_1px_#d5d2cd]',
    dot: null,
  },
  newRequest: { box: 'bg-surface-green10 text-green-deep04', dot: null },
  green: {
    box: 'bg-surface-green10 text-green-deep04 shadow-[inset_0_0_0_1px_rgba(0,158,79,0.4)]',
    dot: 'bg-green-deep02',
  },
  accent: {
    box: 'bg-functional-accent01 text-functional-accent04 shadow-[inset_0_0_0_1px_#e3b5f9]',
    dot: 'bg-functional-accent03',
  },
  warning: {
    box: 'bg-functional-warning01 text-functional-warning04 shadow-[inset_0_0_0_1px_#ffdf56]',
    dot: 'bg-functional-warning03',
  },
  success: {
    box: 'bg-functional-success01 text-functional-success04 shadow-[inset_0_0_0_1px_#7eeac9]',
    dot: 'bg-functional-success03',
  },
  failed: {
    box: 'bg-functional-failed01 text-functional-failed04 shadow-[inset_0_0_0_1px_#ff7474]',
    dot: 'bg-functional-failed03',
  },
};

/** 상태 칩. "New request" 는 상태가 아니라 알림이라 점 없이 그린다 */
export const StatusChip = ({ chip, className }) => {
  const tone = TONE[chip.tone] ?? TONE.gray;
  return (
    <span
      className={cn(
        'inline-flex h-[22px] min-w-0 max-w-full items-center gap-1.5 whitespace-nowrap rounded-md px-2 typo-b12Strong',
        tone.box,
        className,
      )}
    >
      {tone.dot && <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', tone.dot)} />}
      <span className='truncate pt-0.5 leading-[14px]'>{chip.label}</span>
    </span>
  );
};

/** 카운트 배지 — 사이드바 · 탭 · Inbox 공통 (failed03 · 18px · 11/500), 늘어나면 한 번 튄다 */
export const CountBadge = ({ count, title, className }) => {
  const prev = useRef(count);
  const [bump, setBump] = useState(0);
  useEffect(() => {
    if (count > prev.current) setBump((b) => b + 1);
    prev.current = count;
  }, [count]);
  if (!(count > 0)) return null;
  return (
    <span
      key={bump}
      title={title}
      className={cn(
        'inline-flex h-[18px] min-w-[18px] shrink-0 items-center justify-center rounded-full bg-functional-failed03 px-[5px] text-[11px] font-medium leading-none text-grayscale-gray00 tabular-nums',
        bump > 0 && 'td-bump',
        className,
      )}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
};

/** 모바일 Menu 버튼 배지 (워크보드·Ops 공통) */
export const MenuBadge = ({ count = 0, status = false }) => {
  const n = Math.max(0, count | 0);
  if (n > 0) {
    return (
      <span
        aria-hidden
        className='absolute -right-1.5 -top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-functional-failed03 px-[5px] text-[11px] font-medium leading-none text-grayscale-gray00 tabular-nums shadow-[0_0_0_2px_#ffffff]'
      >
        {n > 99 ? '99+' : n}
      </span>
    );
  }
  if (!status) return null;
  return (
    <span
      aria-hidden
      className='absolute right-0.5 top-1.5 h-2 w-2 rounded-full bg-functional-failed03 shadow-[0_0_0_2px_#ffffff]'
    />
  );
};

export const menuBadgeLabel = (
  count = 0,
  status = false,
  statusText = 'Complete project needs attention',
) => {
  const n = Math.max(0, count | 0);
  if (n > 0) return `Open menu · ${n} new`;
  return status ? `Open menu · ${statusText}` : 'Open menu';
};

/** 아직 열어보지 않은 새 배정 표시 */
export const UnreadDot = () => (
  <span
    title='New request · not opened yet'
    className='h-2 w-2 shrink-0 rounded-full bg-functional-failed03'
  />
);

export const MemberAvatar = ({ member, className }) => (
  <span
    title={member?.name}
    className={cn(
      'flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-surface-green10 text-[10px] font-medium text-green-deep04 shadow-[0_0_0_2px_#ffffff]',
      className,
    )}
  >
    {member ? initials(member.name) : '?'}
  </span>
);

/**
 * 오른쪽 화살표 — 글자(→) 대신 그린다. 글자는 Safari 에서 안 보일 때가 있다.
 * 방향은 className 의 rotate 로 (예: -rotate-90 = 위)
 */
export const ArrowIcon = ({ className }) => (
  <svg
    aria-hidden
    viewBox='0 0 16 16'
    fill='none'
    stroke='currentColor'
    strokeWidth='1.5'
    strokeLinecap='round'
    strokeLinejoin='round'
    className={cn('shrink-0', className)}
  >
    <path d='M3 8h10M9 4l4 4-4 4' />
  </svg>
);
